import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PDFDownloadLink, Page, Text, View, Document, Image, StyleSheet } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import {
  Pane,
  Heading,
  Text as EvergreenText, // Alias Text from Evergreen UI
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Spinner,
  Alert,
} from 'evergreen-ui';
import './PaymentResponsePage.css';
import logo from '../assets/Chasylmlogo.png';
import watermark from '../assets/Chasylmlogo.png';

function PaymentResponsePage() {
  const [transactionData, setTransactionData] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderTrackingId = searchParams.get('OrderTrackingId');
  const [qrCodeImage, setQrCodeImage] = useState(null);

  useEffect(() => {
    if (transactionData) {
      const qrValue = `
        First Name: ${transactionData?.booking.firstName},
        Last Name: ${transactionData?.booking.lastName},
        Email: ${transactionData?.booking.email},
        Phone: ${transactionData?.booking.phone},
        Destination Name: ${transactionData?.booking.destinationName},
        Amount: ${transactionData?.booking.amount},
        Currency: ${transactionData?.booking.currency},
        Travel Date: ${transactionData?.booking.travel_date},
        Number of Adults: ${transactionData?.booking.adult},
        Number of Children: ${transactionData?.booking.child},
        Remaining Amount: ${transactionData?.booking.remainingAmount},
        Payment Type: ${transactionData?.paymentType},
        Transaction Date: ${transactionData?.created_date},
        Merchant Reference: ${transactionData?.booking.merchant_reference},
        Payment Method: ${transactionData?.payment_method},
        Confirmation Code: ${transactionData?.confirmation_code},
        Payment Status: ${transactionData?.payment_status_description}
      `;

      QRCode.toDataURL(qrValue, { width: 100, margin: 2 }, (err, url) => {
        if (!err) {
          setQrCodeImage(url);
        }
      });
    }
  }, [transactionData]);

  useEffect(() => {
    const fetchTransactionStatus = async () => {
      if (orderTrackingId) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/pesapal/transaction-status?OrderTrackingId=${orderTrackingId}`
          );
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const data = await response.json();
          setTransactionData(data);
          await updateBookingStatus(data);
        } catch (err) {
          setError(err.message);
        }
      }
    };

    const updateBookingStatus = async (data) => {
      try {
        const updateResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/pesapal/update-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderTrackingId: data.order_tracking_id,
            confirmationCode: data.confirmation_code,
            paymentStatusDescription: data.payment_status_description,
            paymentStatusCode: data.status_code,
            createdDate: data.created_date,
            paymentAccount: data.payment_account,
            paymentMethod: data.payment_method,
          }),
        });

        if (!updateResponse.ok) {
          throw new Error(`Failed to update booking status: ${updateResponse.status}`);
        }
      } catch (updateError) {
        console.error('Error updating booking status:', updateError.message);
      }
    };

    fetchTransactionStatus();
  }, [orderTrackingId]);

  const styles = StyleSheet.create({
    page: {
      padding: 30,
      position: 'relative',
      backgroundColor: '#F2EED7',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    contactInfo: {
      top: 20,
      fontSize: 10,
      textAlign: 'right',
    },
    detailsText: {
      fontSize: 12,
    },
    logo: {
      width: 100,
      height: 80,
      marginBottom: 10,
    },
    watermark: {
      position: 'absolute',
      width: '90%',
      height: '60%',
      opacity: 0.1,
      top: 100,
      left: 0,
    },
    table: {
      display: 'table',
      width: '100%',
      borderStyle: 'solid',
      borderWidth: 1,
      margin: '10px 0',
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableCol: {
      width: '16.66%',
      borderStyle: 'solid',
      borderWidth: 1,
      padding: 8,
    },
    tableCell: {
      margin: 'auto',
      fontSize: 10,
    },
    title: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 10,
    },
    section: {
      marginBottom: 10,
    },
    qrCodeContainer: {
      textAlign: 'start',
      marginTop: 20,
      marginBottom: 20,
    },
    qrCode: {
      width: 100,
      height: 100,
      marginTop: 10,
    },
  });

  const MyDocument = () => (
    <Document>
      <Page style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.contactInfo}>
            <Text>info@chasylmtours.com</Text>
            <Text>Tel: +254720727776</Text>
            <Text>Nairobi, Kenya</Text>
          </View>
        </View>
        <Image src={watermark} style={styles.watermark} />
        <Text style={styles.title}>Chasylm Tours & Safaris Receipt</Text>
        <View style={styles.section}>
          <Text style={styles.detailsText}>
            Name: {transactionData?.booking.firstName} {transactionData?.booking.lastName}
          </Text>
          <Text style={styles.detailsText}>Phone: {transactionData?.booking.phone}</Text>
          <Text style={styles.detailsText}>Email: {transactionData?.booking.email}</Text>
          <Text style={styles.detailsText}>Destination: {transactionData?.booking.destinationName}</Text>
          <Text style={styles.detailsText}>Travel Date: {transactionData?.booking.travel_date}</Text>
        </View>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Payment Method</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Amount (USD)</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Merchant Reference</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Payment Date</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>No. of Adult(s)</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>No. of Child(ren)</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData?.payment_method}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData?.booking.amount}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData?.booking.merchant_reference}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData.created_date}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData?.booking.adult}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>{transactionData?.booking.child}</Text>
            </View>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.detailsText}>Tracking ID: {transactionData.order_tracking_id}</Text>
          <Text style={styles.detailsText}>Payment Account: {transactionData.payment_account}</Text>
          <Text style={styles.detailsText}>Confirmation Code: {transactionData.confirmation_code}</Text>
          <Text style={styles.detailsText}>Balance: {transactionData?.booking.remainingAmount}</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.detailsText}>Message: {transactionData.message}</Text>
        </View>
        {qrCodeImage && (
          <View style={styles.qrCodeContainer}>
            <Text style={styles.detailsText}>Scan for more details</Text>
            <Image src={qrCodeImage} style={styles.qrCode} />
          </View>
        )}
        <Text style={styles.detailsText}>Thank you for booking with us!</Text>
      </Page>
    </Document>
  );

  if (error) {
    return (
      <Pane display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert intent="danger" title="Error" marginBottom={32}>
          {error}
        </Alert>
      </Pane>
    );
  }

  if (!transactionData) {
    return (
      <Pane display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner />
        <EvergreenText marginLeft={16}>Loading transaction status...</EvergreenText>
      </Pane>
    );
  }

  return (
    <Pane
      elevation={1}
      padding={24}
      marginX="auto"
      marginTop={80}
      marginBottom={16}
      maxWidth={800}
      background="tint2"
      borderRadius={8}
    >
      <Heading size={800} marginBottom={16}>
        Transaction Status
      </Heading>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Detail</TableHeaderCell>
            <TableHeaderCell>Value</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Merchant Reference</TableCell>
            <TableCell>{transactionData.merchant_reference}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Amount</TableCell>
            <TableCell>
              {transactionData.amount} {transactionData.currency}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>{transactionData.payment_status_description}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Payment Method</TableCell>
            <TableCell>{transactionData.payment_method}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Transaction Date</TableCell>
            <TableCell>{new Date(transactionData.created_date).toLocaleString()}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Confirmation Code</TableCell>
            <TableCell>{transactionData.confirmation_code}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Pane marginTop={16}>
        <PDFDownloadLink document={<MyDocument />} fileName="receipt.pdf">
          {({ loading }) => (
            <Button appearance="primary" isLoading={loading}>
              {loading ? 'Loading document...' : 'Download Receipt'}
            </Button>
          )}
        </PDFDownloadLink>
      </Pane>
    </Pane>
  );
}

export default PaymentResponsePage;